import { useState, useEffect, useCallback, useRef } from 'react';
import { isApiError } from '@/api/client';

type QueryFn<T> = (signal: AbortSignal) => Promise<T>;

interface UseApiQueryOptions<T> {
  queryFn: QueryFn<T>;
  mockData?: T;
  enabled?: boolean;
}

interface UseApiQueryResult<T> {
  data: T | undefined;
  isLoading: boolean;
  error: string | undefined;
  isUsingMockData: boolean;
  reload: () => void;
}

export const useApiQuery = <T>(
  options: UseApiQueryOptions<T>,
  deps: unknown[] = [],
): UseApiQueryResult<T> => {
  const { queryFn, mockData, enabled = true } = options;

  const [data, setData] = useState<T | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [isUsingMockData, setIsUsingMockData] = useState(false);
  const abortRef = useRef<AbortController>();

  const load = useCallback(async () => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(undefined);

    try {
      const result = await queryFn(controller.signal);

      if (!controller.signal.aborted) {
        setData(result);
        setIsUsingMockData(false);
      }
    } catch (err) {
      if (controller.signal.aborted) return;

      // Mock data is a local-development convenience only. In any built/deployed
      // environment (dev cluster, qa, prod) `import.meta.env.DEV` is false, so a
      // failed request surfaces a real error instead of silently showing fake data.
      if (mockData !== undefined && import.meta.env.DEV) {
        setData(mockData);
        setIsUsingMockData(true);
      } else {
        let message: string;
        if (isApiError(err)) {
          message =
            err.status === 403
              ? 'You do not have permission to view this resource.'
              : err.message;
        } else if (
          typeof err === 'object' &&
          err !== null &&
          'message' in err &&
          typeof (err as { message: unknown }).message === 'string'
        ) {
          message = (err as { message: string }).message;
        } else {
          message = 'An unexpected error occurred';
        }
        setError(message);
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  useEffect(() => {
    load();

    return () => {
      abortRef.current?.abort();
    };
  }, [load]);

  return { data, isLoading, error, isUsingMockData, reload: load };
};
